import { forwardRef } from 'react';
import type { DOMAttributes, ReactNode } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import type { TooltipProps } from '../GraphModal/Tooltip';
import { Tooltip } from '../GraphModal/Tooltip';
import { IconAccept, IconDeny } from '../icons/Icons';

const approvalMessages = defineMessages({
  deny: { defaultMessage: 'Refuser', id: 'zIfkSW' },
  accept: { defaultMessage: 'Accepter', id: '56kk/w' },
});

const approvalTextButtonClassNames = {
  deny: 'text-primary-base no-underline hover:underline',
  accept: 'text-gray-dark no-underline hover:text-primary-base hover:underline',
};

const approvalButtonIcons = {
  deny: <IconDeny />,
  accept: <IconAccept />,
};

type SimpleButtonRawType = {
  content: ReactNode;
  onClick: DOMAttributes<HTMLButtonElement>['onClick'];
  className?: string;
};

type SimpleApprovalButtonTextType = Omit<SimpleButtonRawType, 'content'> & {
  decision: 'accept' | 'deny';
};

type SimpleApprovalButtonIconType = Omit<SimpleButtonRawType, 'content'> & {
  decision: 'accept' | 'deny';
  withTooltip?: Pick<TooltipProps, 'content' | 'placement'>;
};

type SimpleApprovalButtonType = (SimpleApprovalButtonTextType | SimpleApprovalButtonIconType) & {
  buttonType: 'text' | 'icon';
};

const SimpleButton = forwardRef<HTMLButtonElement, SimpleButtonRawType>((props, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      className={twMerge('SimpleButton', props.className)}
      onClick={props.onClick}
    >
      {props.content}
    </button>
  );
});

const SimpleApprovalButtonText = ({
  decision,
  className,
  onClick,
  ...otherProps
}: SimpleApprovalButtonTextType) => {
  return (
    <SimpleButton
      content={<FormattedMessage {...approvalMessages[decision]} />}
      className={twMerge('TextButton', approvalTextButtonClassNames[decision], className)}
      onClick={onClick}
      {...otherProps}
    />
  );
};

const SimpleApprovalButtonIcon = ({
  decision,
  className,
  onClick,
  withTooltip,
  ...otherProps
}: SimpleApprovalButtonIconType) => {
  const iconButton = (
    <SimpleButton
      content={approvalButtonIcons[decision]}
      className={twMerge('IconButton opacity-50 hover:opacity-100', className)}
      onClick={onClick}
      {...otherProps}
    />
  );

  return withTooltip ? (
    <Tooltip
      content={<FormattedMessage {...approvalMessages[decision]} />}
      placement={withTooltip.placement}
    >
      {iconButton}
    </Tooltip>
  ) : (
    iconButton
  );
};

export const SimpleApprovalButton = (props: SimpleApprovalButtonType) => {
  const Component =
    props.buttonType === 'icon' ? SimpleApprovalButtonIcon : SimpleApprovalButtonText;
  return <Component {...props} />;
};
